import Modal, { useAsyncModal } from '@/libs/antd/components/Modal';
import FleetTitle from '@/libs/antd/components/Title';
import { useForm } from '@/libs/antd/form';
import ANTD_DEFAULT_PROPS from '@/libs/antd/props';
import rule from '@/libs/antd/rules';
import { FileData } from '@/libs/fleet-api/services/types';
import { Col, Divider, Form, Row, Upload, UploadFile } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { FC } from 'react';
import { useMutation } from '@tanstack/react-query';
import { uploader } from '@/libs/fleet-api/uploader';
import { UploadAgreementDocumentPayload } from '@/libs/fleet-api/services/print';

export const useUploadAgreementModal = () =>
	useAsyncModal<
		{
			id: number;
			customerId?: string;
		},
		UploadAgreementDocumentPayload
	>();

export type UploadAgreementModalProps = ReturnType<
	typeof useUploadAgreementModal
>;

const UploadAgreementModal: FC<UploadAgreementModalProps> = ({
	data,
	modal,
	close,
}) => {
	const { form, fieldName } = useForm<UploadAgreementDocumentPayload>();
	const uploadMutation = useMutation({
		mutationFn: uploader,
	});

	const { customerId } = data ?? {};

	return (
		<Modal
			{...modal}
			width={940}
			closable={false}
			buttonPosition="end"
			cancelText="CANCEL"
			okText="SAVE"
			okButtonProps={{
				disabled: uploadMutation.isPending,
				loading: uploadMutation.isPending,
			}}
			onCancel={() => close()}
			onOk={form.submit}
			afterOpenChange={() => form.resetFields()}
		>
			<div style={{ marginBottom: 20 }}>
				<div style={{ marginBottom: 20 }}>
					<FleetTitle>Upload Dokumen Perjanjian Kontrak</FleetTitle>
				</div>
				<Form
					{...ANTD_DEFAULT_PROPS.FORM}
					form={form}
					onFinish={close}
				>
					<Form.Item
						name={fieldName('id')}
						initialValue={data?.id}
						noStyle
					/>
					<Row gutter={40}>
						<Col span={12}>
							<Form.Item
								name={fieldName('agreementDocument')}
								label="Upload Dokumen Perjanjian Kontrak"
								rules={[rule.fileRequired()]}
								labelCol={{ span: 12 }}
								wrapperCol={{ span: 12 }}
								getValueProps={(value) => {
									const file = value as FileData;
									return {
										fileList: file?.id
											? ([
													{
														uid: file.id,
														name: file.name,
														thumbUrl: file.url,
													},
												] as UploadFile[])
											: [],
									};
								}}
								normalize={({ file }: { file: UploadFile }) => {
									return {
										id: file.uid,
										format: file.type,
										name: file.name,
										url: file.thumbUrl,
									} as FileData;
								}}
							>
								<Upload.Dragger
									listType="picture"
									maxCount={1}
									accept=".jpg,.jpeg,.png,.pdf"
									beforeUpload={() => false}
									onRemove={() => {
										form.setFieldValue(fieldName('agreementDocument'), null);
										return false;
									}}
									onChange={async ({ file }) => {
										const fileid = Date.now().toString();
										const filepath = `customer/${customerId}/agreement`;

										try {
											const url = await uploadMutation.mutateAsync({
												file: file as unknown as File,
												fileid,
												filepath,
											});

											form.setFieldValue(fieldName('agreementDocument'), {
												format: 'jpeg',
												id: fileid,
												name: file.name,
												path: filepath,
												url,
											});
										} catch {
											form.setFieldValue(fieldName('agreementDocument'), null);
										}
									}}
								>
									<UploadOutlined
										style={{
											fontSize: 25,
											color: '#BABABA',
											display: 'block',
										}}
									/>
								</Upload.Dragger>
							</Form.Item>
						</Col>
					</Row>
					<Divider />
					<Row gutter={40}>
						<Col span={12}>
							<Form.Item
								name={fieldName('directorStatementDocument')}
								label="Upload Dokumen Surat Pernyataan Data Korporasi"
								rules={[rule.fileRequired()]}
								labelCol={{ span: 12 }}
								wrapperCol={{ span: 12 }}
								getValueProps={(value) => {
									const file = value as FileData;
									return {
										fileList: file?.id
											? ([
													{
														uid: file.id,
														name: file.name,
														thumbUrl: file.url,
													},
												] as UploadFile[])
											: [],
									};
								}}
								normalize={({ file }: { file: UploadFile }) => {
									return {
										id: file.uid,
										format: file.type,
										name: file.name,
										url: file.thumbUrl,
									} as FileData;
								}}
							>
								<Upload.Dragger
									listType="picture"
									maxCount={1}
									accept=".jpg,.jpeg,.png,.pdf"
									beforeUpload={() => false}
									onRemove={() => {
										form.setFieldValue(
											fieldName('directorStatementDocument'),
											null,
										);
										return false;
									}}
									onChange={async ({ file }) => {
										const fileid = Date.now().toString();
										const filepath = `customer/${customerId}/agreement`;

										try {
											const url = await uploadMutation.mutateAsync({
												file: file as unknown as File,
												fileid,
												filepath,
											});

											form.setFieldValue(
												fieldName('directorStatementDocument'),
												{
													format: 'jpeg',
													id: fileid,
													name: file.name,
													path: filepath,
													url,
												},
											);
										} catch {
											form.setFieldValue(
												fieldName('directorStatementDocument'),
												null,
											);
										}
									}}
								>
									<UploadOutlined
										style={{
											fontSize: 25,
											color: '#BABABA',
											display: 'block',
										}}
									/>
								</Upload.Dragger>
							</Form.Item>
						</Col>
						<Col span={12}>
							<Form.Item
								name={fieldName('commissionerStatementDocument')}
								label="Upload Dokumen Surat Persetujuan Komisaris/RUPS"
								rules={[rule.fileRequired()]}
								labelCol={{ span: 12 }}
								wrapperCol={{ span: 12 }}
								getValueProps={(value) => {
									const file = value as FileData;
									return {
										fileList: file?.id
											? ([
													{
														uid: file.id,
														name: file.name,
														thumbUrl: file.url,
													},
												] as UploadFile[])
											: [],
									};
								}}
								normalize={({ file }: { file: UploadFile }) => {
									return {
										id: file.uid,
										format: file.type,
										name: file.name,
										url: file.thumbUrl,
									} as FileData;
								}}
							>
								<Upload.Dragger
									listType="picture"
									maxCount={1}
									accept=".jpg,.jpeg,.png,.pdf"
									beforeUpload={() => false}
									onRemove={() => {
										form.setFieldValue(
											fieldName('commissionerStatementDocument'),
											null,
										);
										return false;
									}}
									onChange={async ({ file }) => {
										const fileid = Date.now().toString();
										const filepath = `customer/${customerId}/agreement`;

										try {
											const url = await uploadMutation.mutateAsync({
												file: file as unknown as File,
												fileid,
												filepath,
											});

											form.setFieldValue(
												fieldName('commissionerStatementDocument'),
												{
													format: 'jpeg',
													id: fileid,
													name: file.name,
													path: filepath,
													url,
												},
											);
										} catch {
											form.setFieldValue(
												fieldName('commissionerStatementDocument'),
												null,
											);
										}
									}}
								>
									<UploadOutlined
										style={{
											fontSize: 25,
											color: '#BABABA',
											display: 'block',
										}}
									/>
								</Upload.Dragger>
							</Form.Item>
						</Col>
					</Row>
					<Divider />
					<Row gutter={40}>
						<Col span={12}>
							<Form.Item
								name={fieldName('otherDocument')}
								label="Upload Dokumen Lainnya"
								labelCol={{ span: 12 }}
								wrapperCol={{ span: 12 }}
								getValueProps={(value) => {
									const file = value as FileData;
									return {
										fileList: file?.id
											? ([
													{
														uid: file.id,
														name: file.name,
														thumbUrl: file.url,
													},
												] as UploadFile[])
											: [],
									};
								}}
								normalize={({ file }: { file: UploadFile }) => {
									return {
										id: file.uid,
										format: file.type,
										name: file.name,
										url: file.thumbUrl,
									} as FileData;
								}}
							>
								<Upload.Dragger
									listType="picture"
									maxCount={1}
									accept=".jpg,.jpeg,.png,.pdf"
									beforeUpload={() => false}
									onRemove={() => {
										form.setFieldValue(fieldName('otherDocument'), null);
										return false;
									}}
									onChange={async ({ file }) => {
										const fileid = Date.now().toString();
										const filepath = `customer/${customerId}/agreement`;

										try {
											const url = await uploadMutation.mutateAsync({
												file: file as unknown as File,
												fileid,
												filepath,
											});

											form.setFieldValue(fieldName('otherDocument'), {
												format: 'jpeg',
												id: fileid,
												name: file.name,
												path: filepath,
												url,
											});
										} catch {
											form.setFieldValue(fieldName('otherDocument'), null);
										}
									}}
								>
									<UploadOutlined
										style={{
											fontSize: 25,
											color: '#BABABA',
											display: 'block',
										}}
									/>
								</Upload.Dragger>
							</Form.Item>
						</Col>
					</Row>
				</Form>
			</div>
		</Modal>
	);
};

export default UploadAgreementModal;
